import Button, {
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import { searchResult } from "@booking/exampleData";
import { useScreenSize } from "@booking/hooks/useScreenSize";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import type { SearchResult } from "@search/api/types";
import { useSearchQuery } from "@search/api/useSearchQuery";
import FilterPills from "@search/components/FilterPills";
import { Pagination } from "@search/components/Pagination";
import SortFilter from "@search/components/SortFilter";
import type { ResultCardProps } from "@search/components/constructs/ResultCard";
import { FilterList } from "@search/components/containers/FilterList";
import { ResultList } from "@search/components/containers/ResultList";
import { useGaViewItemList } from "@search/hook/useGaViewItemList";
import { FilterSet } from "@search/store/FilterSet";
import { useSearchStore } from "@search/store/useSearchStore";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import Icon from "@/booking/components/atoms/media/Icon";
import Dialog from "@/components/containers/Dialog";
import { Loading } from "@/components/molecules/Loading";
import { ColorEnum } from "@/enums";
import { openDialog } from "@/utils/helpers";

import styles from "./Results.module.css";

const FILTERS_DIALOG_ID = "filters-dialog";
const ref = "results";

function Results() {
  const {
    counts,
    limitPerPage,
    pageCount,
    isSearchLoading: isLoading,
    nameResources,
    results: searchResults,
    suggestions,
    totalResults,
  } = useSearchQuery();
  const { filters, updateFilterFromQuery } = useSearchStore((state) => state);
  const { isMinLg } = useScreenSize();
  const filterSet = new FilterSet(filters);

  // to add to result data
  const { venue } = searchResult;

  const dialogHeader = (
    <React.Fragment>
      <p className={"text-xl font-bold"}>Filters</p>

      <FilterPills filters={filters} nameResources={nameResources} />
    </React.Fragment>
  );

  const mapToResultCards = (result: SearchResult): ResultCardProps => {
    const toFix = {
      breadcrumbs: result.breadcrumb,
      countryCode: result.countryCode,
      distance: result.distance,
      id: result.venueId,
      image: {
        alt: result.venueName,
        src: result.image,
      },
      labels: result.labels,
      name: result.venueName,
      rating: result.reviewRating,
      ratingCount: result.numberOfReviews,
      slug: result.slug,
      totalPackageCount: result.totalPackageCount,
    };

    return {
      details: {
        ...result,
        venue: {
          ...venue,
          ...toFix,
        },
      },
      dateParam: filterSet.getDateParam(),
      flightsParam: filterSet.getFlights(),
      nightsParam: filterSet.getNights(),
    };
  };

  const results: Array<ResultCardProps> = searchResults.map(mapToResultCards);
  const suggestedResults: Array<ResultCardProps> =
    suggestions.map(mapToResultCards);

  const { search } = useLocation();

  useEffect(() => {
    updateFilterFromQuery(search);
  }, [updateFilterFromQuery, search]);

  useGaViewItemList(searchResults, filters, nameResources);

  return (
    <section className={styles[ref]}>
      {!isMinLg && (
        <section className={styles["mobile-header"]}>
          <Button
            color={ColorEnum.DARKGREEN}
            variant={ButtonVariantEnum.SOLID}
            onClick={() => openDialog(FILTERS_DIALOG_ID)}
          >
            <Icon name={faFilter} noPadding />
            <span>Filters</span>
          </Button>

          <SortFilter filters={filters} />
        </section>
      )}

      {isMinLg && (
        <FilterList
          counts={counts}
          filters={filters}
          totalResults={totalResults}
          isLoading={isLoading}
        />
      )}

      <ResultList
        filters={filters}
        limitPerPage={limitPerPage}
        nameResources={nameResources}
        results={results}
        suggestions={suggestedResults}
        totalResults={totalResults}
      />

      {pageCount > 1 && !isMinLg && (
        <Pagination margin={2} filters={filters} range={3} />
      )}
      {pageCount > 1 && isMinLg && <Pagination filters={filters} />}

      {!isMinLg && (
        <Dialog fullscreen id={FILTERS_DIALOG_ID} header={dialogHeader} modal>
          <FilterList
            counts={counts}
            filters={filters}
            totalResults={totalResults}
            isLoading={isLoading}
          />
        </Dialog>
      )}

      {isLoading && <Loading message="Searching for great golf trips" />}
    </section>
  );
}

export default Results;
export { Results };
